import { authentication } from '@microsoft/teams-js';
import { instance } from '../../apis/baseApi';
import { config } from '../../config/configurations';
import { getCookie, isCookieSetAndValid, setCookieForTeams } from '@common/helpers/cookies';

export const getSBToken = async (token: string | null) => {
  const base_url = `${config.REACT_APP_URL_BACKEND_API}/${config.REACT_APP_API_VERSION}/authentication/oauth2/grant`;
  if (!token) {
    return;
  }
  return instance.post(base_url, {
    grant_type: 'sso_login',
    provider: 'microsoft',
    token: token,
    mode: 'teams',
  });
};

let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 2;

export const getMSAccessToken = async () => {
  const isOnMSConfigPages =
    localStorage.getItem('integrationMode') === 'teams' &&
    (window.location.pathname === '/overview' || window.location.pathname === '/config');
  const msToken = null;
  if (!isOnMSConfigPages) {
    return msToken;
  }
  const isCookieValid = isCookieSetAndValid('ms-token');
  const token = isCookieValid
    ? getCookie('ms-token')
    : await authentication.getAuthToken({ silent: true });
  return token;
};

export const refreshMS = async () => {
  if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
    refreshAttempts = 0;
    throw new Error('Max refresh attempts reached');
  }

  refreshAttempts++;

  const token = await getMSAccessToken();
  const response = await getSBToken(token);
  if (!response) {
    return;
  }
  if (response.status === 200) {
    document.cookie = `x-access-token=${response.data.access_token};SameSite=None;Secure;Domain=.${window.location.hostname.split('.').slice(1).join('.')}`;
    refreshAttempts = 0;
    return response;
  } else if (response.status === 302) {
    window.location.href = response.data.redirect_url;
    return;
  }
  throw new Error('Failed to get SB token');
};

/**
 * Renew the access token from the Teams identity rather than from the
 * refresh session. A Teams webview may drop the cookies of the API, and
 * with them the session that carries the refresh token. The SSO token is
 * short-lived and bound to our audience, so it is the one-time credential
 * of this environment; the refresh token never leaves the server.
 */
export const renewFromTeamsSso = async () => {
  const token = await authentication.getAuthToken({ silent: true });
  const response = await getSBToken(token);
  if (!response || response.status !== 200 || !response.data?.access_token) {
    throw new Error('Teams SSO renewal failed');
  }
  setCookieForTeams('x-access-token', response.data.access_token);
  return response.data;
};
